
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AIPrediction } from "@/services/predictions/predictionsService";
import { ArrowUp, ArrowDown, Minus, TrendingUp, Wifi, WifiOff, Clock } from "lucide-react";
import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { useSpotPrices } from "@/hooks/useSpotPrices";

interface PredictionCardProps {
  prediction: AIPrediction;
}

interface ParsedPrediction {
  previsione?: string;
  markdown?: string;
  affidabilità?: string;
  data_previsione?: string;
}

export function PredictionCard({ prediction }: PredictionCardProps) {
  // Hook per i prezzi spot in tempo reale
  const { data: spotPricesData } = useSpotPrices();

  // Estrai i dati dal campo prediction - gestisce sia JSON che testo diretto
  const parsedContent = useMemo(() => {
    try {
      // Se il campo prediction è una stringa che inizia con '{', prova il parsing JSON
      if (typeof prediction.prediction === 'string' && prediction.prediction.trim().startsWith('{')) {
        const parsed = JSON.parse(prediction.prediction) as ParsedPrediction;
        return parsed;
      }
      // Altrimenti tratta come testo diretto
      return null;
    } catch (e) {
      console.log('Campo prediction contiene testo diretto, non JSON:', prediction.prediction);
      return null;
    }
  }, [prediction.prediction]);

  const trendIcon = {
    UP: <ArrowUp className="h-4 w-4 text-green-500" />,
    DOWN: <ArrowDown className="h-4 w-4 text-red-500" />,
    STABLE: <Minus className="h-4 w-4 text-yellow-500" />
  }[prediction.price_trend];

  const trendColor = {
    UP: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    DOWN: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    STABLE: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
  }[prediction.price_trend];

  const formatMetalType = (metal: string) => {
    if (metal === 'ORO') return 'Oro';
    if (metal === 'ARGENTO') return 'Argento';
    return metal;
  };

  const formatPredictionDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat('it-IT', { 
        day: '2-digit', 
        month: 'long', 
        year: 'numeric' 
      }).format(date);
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateStr;
    }
  };

  // Calcola la percentuale di affidabilità
  const confidencePercentage = useMemo(() => {
    if (parsedContent?.affidabilità) {
      // Se abbiamo l'affidabilità nel JSON, estrai il numero
      const match = parsedContent.affidabilità.match(/(\d+)%/);
      if (match && match[1]) {
        return parseInt(match[1]);
      }
    }
    
    // Gestisce il confidence_score che può essere già in percentuale o in decimale
    const score = prediction.confidence_score;
    
    // Se il valore è maggiore di 1, assumiamo che sia già in percentuale
    if (score > 1) {
      return Math.round(score);
    }
    
    // Se il valore è tra 0 e 1, lo convertiamo in percentuale
    return Math.round(score * 100);
  }, [parsedContent, prediction.confidence_score]);

  // Determina quale contenuto mostrare
  const displayContent = useMemo(() => {
    if (parsedContent?.markdown) {
      return parsedContent.markdown;
    }
    // Se non c'è JSON o markdown nel JSON, usa il campo prediction direttamente
    return prediction.prediction;
  }, [parsedContent, prediction.prediction]);

  return (
    <Card 
      className="w-full h-full flex flex-col overflow-hidden border-t-4" 
      style={{ borderTopColor: prediction.metal === 'ORO' ? 'hsl(var(--gold))' : 'hsl(var(--silver))' }}
      data-testid={`prediction-card-${prediction.metal.toLowerCase()}`}
    >
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{formatMetalType(prediction.metal)}</span>
          <Badge variant="outline" className={trendColor}>
            <span className="flex items-center gap-1">
              {trendIcon}
              {prediction.price_trend === 'UP' ? 'RIALZO' : 
               prediction.price_trend === 'DOWN' ? 'RIBASSO' : 'STABILE'}
            </span>
          </Badge>
        </CardTitle>
        <CardDescription>
          Previsione del {formatPredictionDate(prediction.prediction_date)}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col justify-between">
        <div className="flex-1">
          <div className="text-sm prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown>{displayContent}</ReactMarkdown>
          </div>
        </div>
        
        {/* Sezione Prezzo Spot in tempo reale */}
        {spotPricesData && (
          <div className="mt-4 p-3 bg-muted/30 rounded-lg border border-border/40">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Prezzo Attuale</span>
              {spotPricesData.isLoading ? (
                <div className="animate-spin h-3 w-3 border border-muted-foreground border-t-transparent rounded-full ml-auto" />
              ) : spotPricesData.lastFetch ? (
                <Wifi className="h-3 w-3 text-green-500 ml-auto" />
              ) : (
                <WifiOff className="h-3 w-3 text-muted-foreground ml-auto" />
              )}
            </div>
            {prediction.metal === 'ORO' && spotPricesData.oro && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Oro:</span>
                <div className="text-right">
                  <div className="text-sm font-bold text-yellow-600 dark:text-yellow-400">
                    €{spotPricesData.oro.price.toFixed(2)}/g
                  </div>
                  {spotPricesData.lastFetch && (
                    <div className="text-xs text-muted-foreground">
                      Aggiornato: {new Intl.DateTimeFormat('it-IT', {
                        hour: '2-digit',
                        minute: '2-digit'
                      }).format(spotPricesData.lastFetch)}
                    </div>
                  )}
                </div>
              </div>
            )}
            {prediction.metal === 'ARGENTO' && spotPricesData.argento && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Argento:</span>
                <div className="text-right">
                  <div className="text-sm font-bold text-gray-600 dark:text-gray-400">
                    €{spotPricesData.argento.price.toFixed(2)}/g
                  </div>
                  {spotPricesData.lastFetch && (
                    <div className="text-xs text-muted-foreground">
                      Aggiornato: {new Intl.DateTimeFormat('it-IT', {
                        hour: '2-digit',
                        minute: '2-digit'
                      }).format(spotPricesData.lastFetch)}
                    </div>
                  )}
                </div>
              </div>
            )}
            {spotPricesData.isLoading && (
              <div className="flex items-center justify-center py-2">
                <div className="text-xs text-muted-foreground">Caricamento prezzi...</div>
              </div>
            )}
            {spotPricesData.error && (
              <div className="flex items-center justify-center py-2">
                <div className="text-xs text-red-500">Errore nel caricamento</div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-4 pt-2 border-t border-border/40">
          <span className="text-xs text-muted-foreground">Affidabilità:</span>
          <Badge variant="secondary" className="font-semibold">
            {confidencePercentage}%
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}
